import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { LocaSmartLayout } from '../../components/locasmart-layout';
import { obterIdTenent, obterEmailUsuario, expiraEm, ehPlanoGratuito, estaEmPeriodoDeTeste } from '../../services/seguranca';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { AxiosError } from 'axios';

const Renovar = () => {
  const dispatch = useAppDispatch();
  const navigate = useHistory();
  const token = useAppSelector((state) => state.token);

  const [pendingInvoice, setPendingInvoice] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [countdown, setCountdown] = useState(300); // 5 min
  const [discount, setDiscount] = useState(0);
  const [couponCode, setCouponCode] = useState('');

  const temPlanoAtivo = !ehPlanoGratuito(token) && !estaEmPeriodoDeTeste(token);
  const dataExpiracao = temPlanoAtivo ? new Date(expiraEm(token)) : null;
  const dataExpiracaoFormatada = dataExpiracao?.toLocaleDateString('pt-BR');

  useEffect(() => {
    const checkInvoice = async () => {
      try {
        setIsLoading(true);
        const tenantId = obterIdTenent(token);

        // Tenta buscar fatura atrasada primeiro (prioridade para inadimplentes)
        try {
          const overdueResponse = await api.get(`/api/payment/subscription/overdue/${tenantId}`);
          if (overdueResponse.data) {
            setPendingInvoice(overdueResponse.data);
            toast.info('Fatura em atraso encontrada. Regularize para continuar usando o serviço.', {
              autoClose: false
            });
            return;
          }
        } catch (overdueError) {
          // Se não encontrou atrasada, segue para buscar pendente
        }

        // Busca fatura pendente (fluxo normal)
        const response = await api.get(`/api/payment/subscription/pending/${tenantId}`);
        if (response.data) {
          setPendingInvoice(response.data);
          toast.info('Fatura pendente encontrada. Por favor, realize o pagamento para continuar usando o serviço.', {
            autoClose: false
          });
        }
      } catch (error) {
        if ((error as AxiosError).response?.status !== 404) {
          console.error('Erro ao verificar fatura:', error);
          toast.error('Ocorreu um erro ao verificar suas faturas');
        }
      } finally {
        setIsLoading(false);
      }
    };

    checkInvoice();
  }, [token]);

  const handlePayInvoice = async () => {
    if (!pendingInvoice) return;

    try {
      setIsProcessingPayment(true);

      if (pendingInvoice.paymentUrl) {
        window.open(pendingInvoice.paymentUrl, '_blank');
      }
      startPaymentMonitoring(pendingInvoice.pedidoId);
    } catch (error) {
      console.error('Erro ao processar pagamento:', error);
      toast.error('Ocorreu um erro ao processar seu pagamento');
      setIsProcessingPayment(false);
    }
  };

  const startPaymentMonitoring = async (pedidoId: string) => {
    const startTime = Date.now();
    const timeout = 300000; // 5 minutos

    while (Date.now() - startTime < timeout) {
      try {
        const response = await api.get(`pedido/${pedidoId}`);
        const { status } = response.data;

        if (status === 'Aprovado') {
          toast.success('Pagamento aprovado com sucesso!');
          handlePaymentSuccess();
          return;
        } else if (status === 'Rejeitado') {
          toast.error('Pagamento recusado');
          setIsProcessingPayment(false);
          return;
        }

        await new Promise(resolve => setTimeout(resolve, 2000));
        setCountdown(prev => prev - 2);
      } catch (error) {
        console.error('Erro ao verificar status:', error);
        break;
      }
    }

    toast.info('Pagamento em análise. Verifique seu e-mail para atualizações.');
    setIsProcessingPayment(false);
  };

  const handlePaymentSuccess = () => {
    if ((pendingInvoice?.totalEmAtraso || 0) > 1) {
      toast.info('Pagamento confirmado! Você ainda possui outras faturas em atraso.');
      setTimeout(() => window.location.reload(), 2000);
      return;
    }
    dispatch({ type: 'SET_BLOQUEADO_INADIMPLENCIA', bloqueado: false });
    dispatch({ type: 'SET_CONTINGENCIA', emContingencia: false, diasAtrasados: 0, diasRestantesContingencia: 0, faturasEmAtraso: 0 });
    setTimeout(() => {
      navigate.push('/minha-conta');
    }, 2000);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatCurrency = (val: number) => {
    return val?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) || '-';
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR');
    } catch {
      return '-';
    }
  };

  if (isLoading) {
    return (
      <LocaSmartLayout>
        <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-blue-50 flex items-center justify-center">
          <div className="text-center">
            <svg className="animate-spin h-10 w-10 text-[#1F467E] mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="mt-4 text-[#1F467E]">Verificando seu status de pagamento...</p>
          </div>
        </div>
      </LocaSmartLayout>
    );
  }

  if (!pendingInvoice) {
    return (
      <LocaSmartLayout>
        <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-blue-50 flex items-center justify-center">
          <div className="text-center bg-white p-8 rounded-lg shadow-md max-w-md w-full">
            <h2 className="text-2xl font-bold text-[#1F467E] mb-4">Nenhuma fatura pendente</h2>
            <p className="text-gray-600 mb-6">Você não possui faturas pendentes no momento.</p>
            <button
              onClick={() => navigate.push('/minha-conta')}
              className="px-6 py-2 bg-[#1F467E] text-white rounded-md hover:bg-blue-800 transition-colors"
            >
              Voltar para Minha Conta
            </button>
          </div>
        </div>
      </LocaSmartLayout>
    );
  }

  return (
    <LocaSmartLayout>
      <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-blue-50 py-8 px-4">
        <div className="container mx-auto">
          <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-md overflow-hidden">
            <div className="bg-[#1F467E] p-6 text-white">
              <h1 className="text-2xl font-bold text-white">Regularize sua assinatura</h1>
              <p className='text-white'>Realize o pagamento para continuar usando o LocaSmart</p>
              {(pendingInvoice.totalEmAtraso || 0) > 1 && (
                <div className="mt-3 inline-block bg-red-600 text-white text-sm font-semibold px-3 py-1 rounded-full">
                  Fatura 1 de {pendingInvoice.totalEmAtraso} em atraso — pagar esta não quita as demais
                </div>
              )}
            </div>

            <div className="p-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h2 className="text-lg font-semibold mb-4">Detalhes da Fatura</h2>
                  <div className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Número do Pedido:</span>
                      <span className="font-medium">{pendingInvoice.pedidoId || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Data de Vencimento:</span>
                      <span className="font-medium">{formatDate(pendingInvoice.dueDate) || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Limite de Veículos:</span>
                      <span className="font-medium">{pendingInvoice.qtdVeiculo || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Descrição:</span>
                      <span className="font-medium">{pendingInvoice.description || '-'}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h2 className="text-lg font-semibold mb-4">Resumo do Pagamento</h2>
                  <div className="space-y-3">
                    <div className="border-t border-gray-200 pt-3 mt-3">
                      <div className="flex justify-between font-semibold">
                        <span>Total:</span>
                        <span className="text-[#1F467E]">
                          {formatCurrency(pendingInvoice.value)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={handlePayInvoice}
                    disabled={isProcessingPayment}
                    className="mt-6 w-full py-3 px-4 bg-green-600 hover:bg-green-700 text-white font-bold rounded-md shadow-md transition-colors flex items-center justify-center"
                  >
                    {isProcessingPayment ? (
                      <>
                        <svg className="animate-spin h-5 w-5 text-white mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processando...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                        Abrir e Pagar Fatura
                      </>
                    )}
                  </button>

                  {pendingInvoice.paymentUrl && !isProcessingPayment && (
                    <div className="mt-4 text-center">
                      <a
                        href={pendingInvoice.paymentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline flex items-center justify-center"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        Abrir Fatura em Nova Janela
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {isProcessingPayment && (
                <div className="mt-6 p-4 bg-blue-50 rounded-md flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 text-[#1F467E] mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Aguardando confirmação do pagamento...
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </LocaSmartLayout>
  );
};

export default Renovar;